import React, { useState, useEffect } from 'react';
import { DataManager } from '../../services/dataManager';
import Button from '../common/Button';
import { DEFAULT_BUENAVISTA_ORG } from '../../constants/peopleCareConstants';

/**
 * Anagrafica Enti & Organizzazioni (es. BuenaVista Cooperativa Sociale)
 * Consente all'Admin di gestire le organizzazioni partner, i referenti/responsabili gerarchici,
 * e i dettagli operativi per i percorsi di counseling organizzativo.
 */
const OrganizationManagementModal = ({ isOpen, onClose, onOrganizationUpdated }) => {
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [newManagerInput, setNewManagerInput] = useState('');

    const [formData, setFormData] = useState({
        id: '',
        name: '',
        shortName: '',
        project: '',
        projectPhase: '',
        defaultManager: '',
        managers: [],
        meetingCount: 8,
        contactEmail: '',
        contactPhone: '',
        notes: ''
    });

    useEffect(() => {
        if (isOpen) {
            loadOrganizations();
        }
    }, [isOpen]);

    const loadOrganizations = async () => {
        try {
            setLoading(true);
            const list = await DataManager.getAllOrganizations();
            setOrganizations(list || [DEFAULT_BUENAVISTA_ORG]);
            if (list && list.length > 0) {
                selectOrg(list[0]);
            } else {
                selectOrg(DEFAULT_BUENAVISTA_ORG);
            }
        } catch (err) {
            console.error("Errore caricamento organizzazioni:", err);
            setOrganizations([DEFAULT_BUENAVISTA_ORG]);
            selectOrg(DEFAULT_BUENAVISTA_ORG);
        } finally {
            setLoading(false);
        }
    };

    const selectOrg = (org) => {
        setSelectedOrg(org);
        setFormData({
            id: org.id || '',
            name: org.name || '',
            shortName: org.shortName || org.name || '',
            project: org.project || 'V!BRA People Care',
            projectPhase: org.projectPhase || 'Fase pilota',
            defaultManager: org.defaultManager || (org.managers && org.managers[0]) || '',
            managers: org.managers || [org.defaultManager || 'Federica Ciabattari'].filter(Boolean),
            meetingCount: org.meetingCount || 8,
            contactEmail: org.contactEmail || '',
            contactPhone: org.contactPhone || '',
            notes: org.notes || ''
        });
        setIsEditing(false);
        setNewManagerInput('');
    };

    const handleCreateNew = () => {
        setSelectedOrg(null);
        setFormData({
            id: `org_${Date.now()}`,
            name: '',
            shortName: '',
            project: 'People Care',
            projectPhase: 'Fase attiva',
            defaultManager: '',
            managers: [],
            meetingCount: 8,
            contactEmail: '',
            contactPhone: '',
            notes: ''
        });
        setIsEditing(true);
        setNewManagerInput('');
    };

    const handleAddManager = () => {
        if (!newManagerInput.trim()) return;
        const updated = [...(formData.managers || []), newManagerInput.trim()];
        setFormData(prev => ({
            ...prev,
            managers: updated,
            defaultManager: prev.defaultManager || newManagerInput.trim()
        }));
        setNewManagerInput('');
    };

    const handleRemoveManager = (mgr) => {
        const updated = (formData.managers || []).filter(m => m !== mgr);
        setFormData(prev => ({
            ...prev,
            managers: updated,
            defaultManager: prev.defaultManager === mgr ? (updated[0] || '') : prev.defaultManager
        }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            alert("Il nome dell'ente è obbligatorio.");
            return;
        }

        try {
            setIsSaving(true);
            await DataManager.saveOrganization(formData);
            await loadOrganizations();
            setIsEditing(false);
            if (onOrganizationUpdated) onOrganizationUpdated();
            alert("✅ Anagrafica dell'ente salvata con successo!");
        } catch (err) {
            console.error("Errore salvataggio ente:", err);
            alert("Errore durante il salvataggio. Riprova.");
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-fade-in overflow-y-auto">
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full my-8 border border-slate-100 flex flex-col max-h-[90vh] overflow-hidden">
                
                {/* Header */}
                <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-slate-50 to-teal-50/40">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-teal-600 text-white flex items-center justify-center text-2xl shadow-md shadow-teal-600/20">
                            🏢
                        </div>
                        <div>
                            <span className="text-[10px] font-black uppercase tracking-widest text-teal-700 bg-teal-100/60 px-2.5 py-0.5 rounded-full">
                                Counseling Organizzativo & Aziendale
                            </span>
                            <h2 className="text-xl font-bold text-slate-800 mt-0.5">
                                Anagrafica Enti & Organizzazioni
                            </h2>
                        </div>
                    </div>
                    <button 
                        onClick={onClose}
                        className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 font-bold flex items-center justify-center transition"
                    >
                        ✕
                    </button>
                </div>

                {/* Body: Split View (List on left, Details/Form on right) */}
                <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
                    
                    {/* Left Sidebar: Organization List */}
                    <div className="w-full md:w-1/3 border-r border-slate-100 p-4 bg-slate-50/50 flex flex-col overflow-y-auto">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Enti Registrati</span>
                            <button
                                onClick={handleCreateNew}
                                className="text-xs font-bold text-teal-600 hover:text-teal-700 bg-teal-50 px-2.5 py-1 rounded-lg border border-teal-200/60 transition"
                            >
                                + Nuovo Ente
                            </button>
                        </div>

                        {loading ? (
                            <p className="text-xs text-slate-400 p-4 text-center">Caricamento enti...</p>
                        ) : (
                            <div className="space-y-2">
                                {organizations.map(org => {
                                    const isSelected = selectedOrg?.id === org.id;
                                    return (
                                        <div
                                            key={org.id}
                                            onClick={() => selectOrg(org)}
                                            className={`p-3.5 rounded-2xl border transition cursor-pointer ${
                                                isSelected 
                                                    ? 'bg-white border-teal-500 shadow-md shadow-teal-500/10 ring-1 ring-teal-500' 
                                                    : 'bg-white/80 border-slate-200/80 hover:bg-white hover:border-slate-300'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <h4 className="font-bold text-sm text-slate-800">{org.name}</h4>
                                                {org.id === 'buenavista' && (
                                                    <span className="text-[9px] font-bold bg-teal-100 text-teal-800 px-2 py-0.5 rounded-full">
                                                        Attivo
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-slate-500 mt-0.5">{org.project || 'V!BRA People Care'}</p>
                                            <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                                                <span>👤</span> {org.defaultManager || 'Nessun referente'}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Right Content: Org Details / Edit Form */}
                    <div className="w-full md:w-2/3 p-6 overflow-y-auto bg-white">
                        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                            <div>
                                <h3 className="text-lg font-bold text-slate-800">
                                    {isEditing ? (selectedOrg ? `Modifica: ${selectedOrg.name}` : 'Crea Nuovo Ente') : formData.name}
                                </h3>
                                <p className="text-xs text-slate-500">
                                    Dettagli operativi e referenti per la somministrazione dei questionari People Care.
                                </p>
                            </div>
                            {!isEditing ? (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition"
                                >
                                    ✏️ Modifica Dati
                                </button>
                            ) : (
                                <button
                                    onClick={() => {
                                        if (selectedOrg) selectOrg(selectedOrg);
                                        else if (organizations.length > 0) selectOrg(organizations[0]);
                                    }}
                                    className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700"
                                >
                                    Annulla
                                </button>
                            )}
                        </div>

                        {/* Form View / Display View */}
                        {isEditing ? (
                            <form onSubmit={handleSave} className="space-y-5">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">Nome Ente / Ragione Sociale *</label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                                            placeholder="es. BuenaVista Cooperativa Sociale"
                                            required
                                            className="w-full p-3 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">Sigla / Nome Breve</label>
                                        <input
                                            type="text"
                                            value={formData.shortName}
                                            onChange={e => setFormData({ ...formData, shortName: e.target.value })}
                                            placeholder="es. BuenaVista"
                                            className="w-full p-3 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">Titolo Progetto</label>
                                        <input
                                            type="text"
                                            value={formData.project}
                                            onChange={e => setFormData({ ...formData, project: e.target.value })}
                                            placeholder="es. V!BRA People Care"
                                            className="w-full p-3 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">Fase Progetto / Edizione</label>
                                        <input
                                            type="text"
                                            value={formData.projectPhase}
                                            onChange={e => setFormData({ ...formData, projectPhase: e.target.value })}
                                            placeholder="es. Fase pilota Buenavista"
                                            className="w-full p-3 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                        />
                                    </div>
                                </div>

                                {/* Managers / Responsabili Gerarchici */}
                                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                                    <label className="block text-xs font-bold text-slate-800 mb-2">
                                        Responsabili Gerarchici & Referenti Aziendali
                                    </label>
                                    <p className="text-[11px] text-slate-500 mb-3">
                                        Questi nominativi appariranno nel menu a tendina quando associ un cliente al percorso organizzativo.
                                    </p>

                                    <div className="flex gap-2 mb-3">
                                        <input
                                            type="text"
                                            value={newManagerInput}
                                            onChange={e => setNewManagerInput(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddManager())}
                                            placeholder="Aggiungi referente (es. Federica Ciabattari)"
                                            className="flex-1 p-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddManager}
                                            className="px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-xs font-bold transition"
                                        >
                                            + Aggiungi
                                        </button>
                                    </div>

                                    {/* List of current managers */}
                                    <div className="flex flex-wrap gap-2">
                                        {(formData.managers || []).map(mgr => (
                                            <span 
                                                key={mgr}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 shadow-sm"
                                            >
                                                <span>👤 {mgr}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveManager(mgr)}
                                                    className="text-slate-400 hover:text-red-500 font-bold ml-1"
                                                >
                                                    ✕
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">Numero Incontri Standard</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="20"
                                            value={formData.meetingCount}
                                            onChange={e => setFormData({ ...formData, meetingCount: parseInt(e.target.value) || 8 })}
                                            className="w-full p-3 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">Email Contatto Ente</label>
                                        <input
                                            type="email"
                                            value={formData.contactEmail}
                                            onChange={e => setFormData({ ...formData, contactEmail: e.target.value })}
                                            placeholder="referente@ente.it"
                                            className="w-full p-3 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1">Note Operative & Convenzione</label>
                                    <textarea
                                        rows="3"
                                        value={formData.notes}
                                        onChange={e => setFormData({ ...formData, notes: e.target.value })}
                                        placeholder="Note sul progetto, accordi di riservatezza, convenzioni..."
                                        className="w-full p-3 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                    />
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                                    <Button
                                        disabled={isSaving}
                                        className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-teal-600/20"
                                    >
                                        {isSaving ? 'Salvataggio...' : '💾 Salva Anagrafica Ente'}
                                    </Button>
                                </div>
                            </form>
                        ) : (
                            /* Read-Only Info Display */
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Progetto Attivo</span>
                                        <p className="font-bold text-slate-800 text-base mt-0.5">{formData.project || 'V!BRA People Care'}</p>
                                        <span className="text-xs text-teal-700 font-semibold">{formData.projectPhase || 'Fase pilota'}</span>
                                    </div>

                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Percorso Standard</span>
                                        <p className="font-bold text-slate-800 text-base mt-0.5">{formData.meetingCount || 8} incontri</p>
                                        <span className="text-xs text-slate-500">Somministrazione IN (1°) e OUT (8°)</span>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                                        Responsabili Gerarchici Registrati ({formData.managers?.length || 0})
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {(formData.managers || []).map(mgr => (
                                            <span 
                                                key={mgr}
                                                className="px-3.5 py-1.5 bg-teal-50 border border-teal-200 text-teal-900 rounded-xl text-xs font-semibold flex items-center gap-1.5"
                                            >
                                                <span>👤</span> {mgr}
                                            </span>
                                        ))}
                                        {(!formData.managers || formData.managers.length === 0) && (
                                            <p className="text-xs text-slate-400 italic">Nessun responsabile specificato.</p>
                                        )}
                                    </div>
                                </div>

                                {formData.notes && (
                                    <div className="p-4 bg-slate-50/80 rounded-2xl border border-slate-100">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Note di Progetto</span>
                                        <p className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed">{formData.notes}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default OrganizationManagementModal;
